import React, { useState, useEffect, useMemo } from 'react'
import shortid from 'shortid'

import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import AddMultiValuesTags from './add-multi-values-tags'
import Button, { ButtonRow } from './button'

import styles from '../component-styles/request-field-modal.module.scss'
import { Label } from './input'
import ConfirmationButtons from './confirmation-buttons'
import SelectBoxTypeahead from './select-box-typeahead'
import {
  sendGeneratorUpdateRequest,
  OptionsProps,
} from '../api-client/track-client'
import { getItemByKeyValue, prepareInput } from '../helpers'
import { ValidationChecks } from '../api-client/api-types'
// import useAPI from '../hooks/useAPI'

interface FieldProps {
  id: string
  newOptions: string[]
  parameterName: string
  fieldName: string
  fieldID: string
}

const defaultField = (): FieldProps => {
  return {
    id: shortid.generate(),
    newOptions: [''],
    parameterName: '',
    fieldName: '',
    fieldID: '',
  }
}

interface Props {
  active: boolean
  toggleActive: () => void
  paramDefs: any[]
  validation?: ValidationChecks[] | undefined | null
  defaultValue?: string | null
}

export default function RequestFieldModal({
  active,
  toggleActive,
  paramDefs,
  validation,
  defaultValue = null,
}: Props): React.ReactElement {
  const [reason, setReason] = useState('')
  const [fields, setFields] = useState([defaultField()])
  const [thanks, setThanks] = useState(false)

  const list = useMemo(
    (): any[] =>
      paramDefs
        .filter((item: any): any => item.fieldType === 'select')
        .map((item: any): string => item.fieldName),
    [paramDefs],
  )
  const onInputChange = (val, index) => {
    const f = fields.concat()
    f[index].parameterName = val
    const found = getItemByKeyValue(
      paramDefs,
      'fieldName',
      fields[index].parameterName,
    )
    if (found !== -1) {
      const { fieldName, fieldID } = found
      f[index].fieldName = fieldName
      f[index].fieldID = fieldID
    }
    setFields(f)
  }

  useEffect(() => {
    if (defaultValue !== '' && defaultValue !== null) {
      onInputChange(defaultValue, 0)
    }
  }, [])

  const onSubmit = async () => {
    const data = {
      changeRequests: fields.map(
        (field: FieldProps): OptionsProps => {
          const { newOptions, fieldName, fieldID } = field
          const useNewOptions = newOptions.map((i) => {
            // eslint-disable-next-line
            // debugger
            const optionValue = prepareInput(i, validation)
            return {
              optionName: i,
              optionValue,
            }
          })
          return {
            newOptions:
              useNewOptions.length === 0
                ? [{ optionName: '', optionValue: '' }]
                : useNewOptions,
            fieldName,
            fieldID,
          }
        },
      ),
      requestNote: reason,
    }
    setThanks(true)
    await sendGeneratorUpdateRequest(data)
  }

  const softDisable = useMemo(() => {
    if (reason !== '') {
      return false
    }
    const fieldsWithValues = fields.filter((f) => f.parameterName !== '')
    return !(fieldsWithValues.length === fields.length)
  }, [reason, fields])

  if (!active) {
    return <></>
  }

  if (thanks) {
    return (
      <>
        <Modal active={active} toggleActive={toggleActive} type="white" narrow>
          <ModalHead>
            <Heading type={3} align="left">
              Request a new value(s)
            </Heading>
          </ModalHead>
          <ModalBody>
            <p>
              We have sent your request for additional values to your account
              admin.
            </p>
          </ModalBody>
          <ModalFooter>
            <ConfirmationButtons
              centerAlign
              type="simple"
              onYes={(): void => {
                toggleActive()
              }}
              yesLabel="OK"
            />
          </ModalFooter>
        </Modal>
      </>
    )
  }

  return (
    <>
      <Modal active={active} toggleActive={toggleActive} type="white" narrow>
        <ModalHead>
          <Heading type={3} align="left">
            Request a new value(s)
          </Heading>
          <p>
            If you need a new value added to the generator you can send a
            request to your admin here.
          </p>
        </ModalHead>
        <ModalBody noPadding>
          <div className={styles.addNameBlockWrapper}>
            {fields.map((field: FieldProps, index: number) => {
              return (
                <div className={styles.addNameBlock} key={field.id}>
                  <Label modalHeading id="parameter-name">
                    Parameter name
                  </Label>
                  <SelectBoxTypeahead
                    id="parameter-name"
                    placeholder="Select parameter or start typing"
                    onInputChange={(val: string): void => {
                      onInputChange(val, index)
                    }}
                    keyName="fieldName"
                    keyValue="fieldID"
                    items={list}
                    value={index === 0 ? defaultValue : ''}
                  />
                  <AddMultiValuesTags
                    transformTags={false}
                    label="Dropdown name(s)"
                    type="modal"
                    onCheckIfExists={(value) => {
                      const data = getItemByKeyValue(
                        paramDefs,
                        'fieldName',
                        fields[index].parameterName,
                      )
                      if (data !== -1) {
                        const fieldValue = getItemByKeyValue(
                          data.selectFields,
                          'optionName',
                          value,
                        )
                        return fieldValue !== -1
                      }
                      return false
                    }}
                    onChange={(val: string[]) => {
                      const f = fields.concat()
                      // remove empty string values
                      f[index].newOptions = val.filter((item) => item !== '')
                      setFields(f)
                    }}
                  />
                  {index > 0 && (
                    <Button
                      type="text"
                      submit
                      className={styles.deleteButton}
                      onClick={() => {
                        const f = fields.concat()
                        f.splice(index, 1)
                        if (f.length === 0) {
                          setFields([defaultField()])
                        } else {
                          setFields(f)
                        }
                      }}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </ModalBody>
        <ModalBody>
          <ButtonRow className={styles.buttonRow}>
            <Button
              type="text"
              submit
              onClick={() => {
                setFields(fields.concat([defaultField()]))
              }}
            >
              Add another +
            </Button>
          </ButtonRow>
          <Label modalHeading id="parameter-reason" optional="(optional)">
            Reason
          </Label>
          <textarea
            id="parameter-reason"
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
              const { value: val } = event.target
              setReason(val)
            }}
            placeholder="I need this value because..."
            value={reason}
          />
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            disabled={softDisable}
            type="simple"
            onYes={(): void => {
              onSubmit()
            }}
            onNo={(): void => {
              toggleActive()
            }}
            yesLabel="Request value(s)"
            noLabel="Go back"
          />
        </ModalFooter>
      </Modal>
    </>
  )
}
